/**
 * Phase 131B — Live viewer count badge
 * Must be rendered as a child of <LiveKitRoom> so useParticipants resolves.
 * Subtracts the broadcaster (local participant on studio side) so academy
 * owners see the actual external audience count.
 */
import { useEffect, useMemo } from 'react';
import { useParticipants, useLocalParticipant } from '@livekit/components-react';
import { Eye } from 'lucide-react';

export default function LivexViewerBadge({
  isBroadcaster = false,
  onChange,
  className = '',
}) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  const count = useMemo(() => {
    // useParticipants includes the local participant. On the studio (broadcaster)
    // side we subtract 1 to show only external viewers; on watch we leave it.
    if (isBroadcaster) {
      // Exclude the local broadcaster identity and any other host identity.
      return participants.filter((p) => p.identity !== localParticipant?.identity).length;
    }
    return participants.length;
  }, [participants, localParticipant, isBroadcaster]);

  // Notify parent (used by studio to drive heartbeat + peak tracking).
  useEffect(() => {
    onChange?.(count);
  }, [count, onChange]);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#07111F] border-2 border-[#22314F] text-white font-bold ${className}`}
      data-testid="livex-viewer-count"
    >
      <Eye className="h-3.5 w-3.5 text-[#FF5A1F]" />
      <span className="text-[13px] tabular-nums" style={{ letterSpacing: '0.02em' }}>{count}</span>
      <span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] font-bold">{count === 1 ? 'viewer' : 'viewers'}</span>
    </span>
  );
}
